import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react"
import "./DialogBox.css"
import { LoaderButton } from "shared/src/components/LoaderButton/LoaderButton"

const DialogContext = createContext(null)

export const DialogBox = ({
	isOpen,
	title,
	message,
	onConfirm,
	onCancel,
	isConfirm,
	confirmButtonText,
	cancelButtontext,
}) => {
	if (!isOpen) return null

	const getConfirmText = () => {
		if (confirmButtonText) return confirmButtonText

		return isConfirm ? "Confirm" : "OK"
	}

	return (
		<div className="hireara-dialogbox-overlay" data-testid="dialogbox">
			<div className="hireara-dialogbox">
				{typeof title === "string" && title.length > 0 && <div className="hireara-dialogbox-title">{title}</div>}
				<div className="hireara-dialogbox-message">{message}</div>
				<div className="hireara-dialogbox-buttons">
					{isConfirm && (
						<LoaderButton className="SquarerLoaderButton" style={{ margin: 0 }} onClick={onCancel}>
							{cancelButtontext || "Cancel"}
						</LoaderButton>
					)}
					<LoaderButton className="SquarerLoaderButton" style={{ margin: 0 }} onClick={onConfirm}>
						<span style={{ fontWeight: 400, color: "var(--ara-purple)" }}>{getConfirmText()}</span>
					</LoaderButton>
				</div>
			</div>
		</div>
	)
}

export const DialogProvider = ({ children }) => {
	const [dialogState, setDialogState] = useState({
		isOpen: false,
		title: null,
		message: null,
		isConfirm: false,
		resolve: null,
		confirmButtonText: null,
		cancelButtontext: null,
	})

	const showAlert = useCallback(
		(message: string | ReactNode, title: string | ReactNode, confirmButtonText?: string, cancelButtontext?: string) => {
			return new Promise((resolve) => {
				setDialogState({
					isOpen: true,
					title,
					message,
					isConfirm: false,
					confirmButtonText,
					cancelButtontext,
					resolve: () => {
						setDialogState((prev) => ({ ...prev, isOpen: false }))
						resolve(true)
					},
				})
			})
		},
		[] // No dependencies, as setDialogState is stable
	)

	const showConfirm = useCallback(
		(message: string | ReactNode, title: string | ReactNode, confirmButtonText?: string, cancelButtontext?: string) => {
			return new Promise((resolve) => {
				setDialogState({
					isOpen: true,
					title,
					message,
					isConfirm: true,
					confirmButtonText,
					cancelButtontext,
					resolve: (value) => {
						setDialogState((prev) => ({ ...prev, isOpen: false }))
						resolve(value)
					},
				})
			})
		},
		[] // No dependencies, as setDialogState is stable
	)

	const handleConfirm = useCallback(() => {
		dialogState.resolve(true)
	}, [dialogState.resolve])

	const handleCancel = useCallback(() => {
		dialogState.resolve(false)
	}, [dialogState.resolve])

	const contextValue = useMemo(() => ({ showAlert, showConfirm }), [showAlert, showConfirm])

	return (
		<DialogContext.Provider value={contextValue}>
			<DialogBox
				isOpen={dialogState.isOpen}
				title={dialogState.title}
				message={dialogState.message}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				isConfirm={dialogState.isConfirm}
				cancelButtontext={dialogState.cancelButtontext}
				confirmButtonText={dialogState.confirmButtonText}
			/>
			{children}
		</DialogContext.Provider>
	)
}

export const useDialog = () => {
	const context = useContext(DialogContext)
	if (!context) {
		throw new Error("useDialog must be used within a DialogProvider")
	}
	return context
}
